import { Check } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { isValidPhoneNumber } from 'libphonenumber-js/min'
import MuiPhoneNumber from 'material-ui-phone-number'
import PropTypes from 'prop-types'
import React from 'react'
import { useLocation, useNavigate } from 'react-router'
import userService from '../../services/user.service'

const DemoRegistrationPage = (props) => {
  const location = useLocation()

  // state
  const [status, setStatus] = React.useState('idle')

  // dialogs
  const successDialog = useDialog(false)
  const errorDialog = useDialog(false)

  // extract email params if passed in url
  const query = new URLSearchParams(location.search)
  const firstnameQuery = query.get('firstname') || ''
  const lastnameQuery = query.get('lastname') || ''
  const emailQuery = query.get('email') || ''
  const companyQuery = query.get('company') || ''
  const phoneQuery = query.get('phone') || ''

  // setup inputs/handlers
  const firstname = useInput(firstnameQuery)
  const lastname = useInput(lastnameQuery)
  const email = useInput(emailQuery)
  const company = useInput(companyQuery)
  const phone = useInput(phoneQuery)

  // clear inputs
  const clearInputs = () =>
    [firstname, lastname, email, company, phone].forEach((item) =>
      item.onChange({ target: { value: '' } })
    )

  // email validation
  const validateEmail = () => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return email.value.length === 0 ? true : email.value.match(emailRegex)
  }

  // phone number validation
  const validatePhoneNumber = () => {
    return phone.value.length === 0 ? true : isValidPhoneNumber(phone.value)
  }

  // check values
  const canSubmit = [
    firstname.value,
    lastname.value,
    email.value,
    company.value,
    phone.value,
    validateEmail(),
    validatePhoneNumber(),
  ].every(Boolean)

  // submit handler
  const handleSubmit = (e) => {
    const submitRequest = async () => {
      try {
        setStatus('pending')
        await userService.submitDemoRegistrationForm({
          firstname: firstname.value,
          lastname: lastname.value,
          email: email.value,
          company: company.value,
          phone: phone.value,
        })
        setStatus('success')
        successDialog.toggle()
        clearInputs()
      } catch {
        setStatus('failed')
        errorDialog.toggle()
      }
    }
    e.preventDefault()
    submitRequest()
  }

  // media query
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'))

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        width: {
          xs: '98vw',
          md: '60vw',
          lg: '50vw',
          xl: '35vw',
        },
        my: '2rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: '100%',
        }}
      >
        <Paper
          elevation={isDesktop ? 1 : 0}
          sx={{
            bgcolor: isDesktop ? 'primary' : '#f6f7f8',
            minWidth: '340px',
            maxWidth: '100%',
            p: {
              xs: '2rem',
              md: '4rem',
            },
            m: {
              xs: '1rem',
              md: '4rem',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 2,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 400 }}>
              Demo ROADS for Free!
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                pb: '2rem',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Check color="success" />
                <Typography variant="body2">Limited Data Access</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Check color="success" />
                <Typography variant="body2">No Credit Card Required</Typography>
              </Box>
            </Box>
            <Grid
              component="form"
              container
              rowSpacing={4}
              columnSpacing={2}
              onSubmit={handleSubmit}
            >
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="John"
                  label="First Name"
                  {...firstname}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Smith"
                  label="Last Name"
                  {...lastname}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Company Name" {...company} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="email"
                  type="email"
                  fullWidth
                  label="Work Email"
                  error={!validateEmail()}
                  helperText={
                    !validateEmail() ? 'Please enter a valid email address' : ''
                  }
                  {...email}
                />
              </Grid>
              <Grid item xs={12}>
                <MuiPhoneNumber
                  fullWidth
                  variant="outlined"
                  defaultCountry="us"
                  countryCodeEditable={false}
                  disableAreaCodes={true}
                  value={phone.value}
                  error={!validatePhoneNumber()}
                  helperText={
                    !validatePhoneNumber()
                      ? 'Please enter a valid phone number'
                      : ''
                  }
                  onChange={(e) => phone.onChange({ target: { value: e } })}
                />
              </Grid>
              <Grid item xs={12}>
                <LoadingButton
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  color="info"
                  disabled={!canSubmit}
                  loading={status === 'pending'}
                  sx={{ height: '50px' }}
                >
                  <Typography
                    sx={{ fontSize: '1.2rem', color: 'white', fontWeight: 500 }}
                  >
                    Get Access!
                  </Typography>
                </LoadingButton>
              </Grid>
            </Grid>
            <Box sx={{}}>
              <Typography variant="body2">
                By submitting this form, you are consenting to receive marketing
                emails from: Quanterion Solutions Incorporated, 266 Genesee
                Street, Utica, NY 13502, US,{' '}
                <Link
                  href="https://www.quanterion.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://www.quanterion.com/
                </Link>
                .
              </Typography>
              <Typography variant="body2" sx={{ mt: '.4rem' }}>
                You can revoke your consent to receive emails at any time by
                using the SafeUnsubscribe® link, found at the bottom of every
                email.{' '}
                <Link
                  href="https://www.quanterion.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Privacy Policy
                </Link>
                ;{' '}
                <Link
                  href="https://www.constantcontact.com/legal/service-provider"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Emails are serviced by Constant Contact
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
      <SuccessDialog {...successDialog} />
      <ErrorDialog {...errorDialog} />
    </Box>
  )
}

/**
 * useInput is a react hook responsible for handling user input states.
 * @param {string} initialValue
 */
const useInput = (initialValue) => {
  const [value, setValue] = React.useState(initialValue)

  const handleChange = (e) => {
    setValue(e.target.value)
  }

  return {
    value,
    onChange: handleChange,
  }
}

/**
 * useDialog is a react hook responsible for handling dialog states.
 * @param {boolean} initialValue
 */
const useDialog = (initialValue) => {
  const [open, setOpen] = React.useState(initialValue)

  const toggle = () => {
    setOpen((open) => !open)
  }

  return {
    open,
    toggle,
  }
}

// SuccessDialog is a ResponseDialog wrapper triggered on success.
const SuccessDialog = ({ open, toggle }) => {
  return (
    <ResponseDialog
      open={open}
      toggle={toggle}
      title={'Success'}
      p1={'Thank you for your interest in ROADS!'}
      p2={
        'Please check your email for further instructions on accessing the ROADS demo.'
      }
      forwardUri={'/'}
    />
  )
}

// ErrorDialog is a Response wrapper triggered on failure.
const ErrorDialog = ({ open, toggle }) => {
  return (
    <ResponseDialog
      open={open}
      toggle={toggle}
      title={'Whoops'}
      p1={'Unfortunately, there was an issue processing your request.'}
      p2={'Please try again later.'}
      forwardUri={'/'}
    />
  )
}

const ResponseDialog = ({ open, toggle, title, p1, p2, forwardUri }) => {
  const navigate = useNavigate()

  const handleClose = () => {
    toggle()
    navigate(forwardUri)
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {p1}
          <br />
          <br />
          {p2}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ResponseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  p1: PropTypes.string.isRequired,
  p2: PropTypes.string.isRequired,
  forwardUri: PropTypes.string.isRequired,
}

export default DemoRegistrationPage
